// ================================
// hooks/useHybridMessaging.js - VERSION COMPLÈTE CORRIGÉE
// ================================

import { useState, useEffect, useRef, useCallback } from 'react';
import io from 'socket.io-client';
import { api } from '../services/api.js';
import { API_BASE_URL } from '../config/apiConfig';

const useHybridMessaging = (onUnreadCountChange) => {
  // ====================================
  // ÉTATS PRINCIPAUX
  // ====================================
  const [conversations, setConversations] = useState([]);
  const [groups, setGroups] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [messages, setMessages] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // États temps réel
  const [isConnected, setIsConnected] = useState(false);
  const [isReconnecting, setIsReconnecting] = useState(false);
  const [typingUsers, setTypingUsers] = useState([]);
  const [onlineUsers, setOnlineUsers] = useState(new Set());
  const [messageReadStatus, setMessageReadStatus] = useState(new Map());

  // États groupes et archivage
  const [groupConversations, setGroupConversations] = useState(new Map());
  const [groupMessages, setGroupMessages] = useState(new Map());
  const [archivedConversations, setArchivedConversations] = useState([]);

  // ====================================
  // RÉFÉRENCES
  // ====================================
  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const isComponentMountedRef = useRef(true);

  // Références Socket
  const isConnectingRef = useRef(false);
  const socketEventListenersRef = useRef(new Set());
  const lastDisconnectReasonRef = useRef(null);
  
  // Références pour le système hybride
  const pollingIntervalRef = useRef(null);
  const syncInProgressRef = useRef(false);
  const lastSyncTimestampRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);
  
  // Références pour éviter les conflits
  const lastKnownMessagesRef = useRef([]);
  const isSocketUpdateRef = useRef(false);

  // ✅ NOUVELLES RÉFÉRENCES POUR STABILITÉ
  const handlersRef = useRef({});
  const selectedConversationRef = useRef(null);

  // ====================================
  // CONFIGURATION HYBRIDE
  // ====================================
  const POLLING_INTERVAL = 30000; // 30 secondes
  const RECONNECT_DELAY = 3000;
  const MAX_RECONNECT_ATTEMPTS = 5;
  const SYNC_ON_FOCUS = true;

  // ====================================
  // FONCTIONS UTILITAIRES DE BASE (DÉCLARER EN PREMIER)
  // ====================================

  const scrollToBottom = useCallback(() => {
    setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  }, []);

  const isUserOnline = useCallback((userId) => {
    return onlineUsers.has(userId);
  }, [onlineUsers]);

  const markMessageAsReadRealtime = useCallback((messageId, conversationId) => {
    if (!socketRef.current) {
      console.warn('⚠️ [Socket] Pas de connexion pour marquage temps réel');
      return;
    }

    console.log(`⚡ [Socket] Émission mark_message_read_realtime: ${messageId}`);
    
    socketRef.current.emit('mark_message_read_realtime', {
      messageId,
      conversationId,
      readAt: new Date().toISOString()
    });
  }, []);

  const markConversationAsReadRealtime = useCallback((conversationId) => {
    if (!socketRef.current) {
      console.warn('⚠️ [Socket] Pas de connexion pour marquage conversation');
      return;
    }

    console.log(`⚡ [Socket] Émission mark_conversation_messages_read_realtime: ${conversationId}`);
    
    socketRef.current.emit('mark_conversation_messages_read_realtime', {
      conversationId,
      readAt: new Date().toISOString()
    });
  }, []);

  const joinConversation = useCallback((conversationId) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('join_conversation', { 
        conversationId 
      });
    }
  }, [isConnected]);

  const leaveConversation = useCallback((conversationId) => {
    if (socketRef.current && isConnected) {
      socketRef.current.emit('leave_conversation', { 
        conversationId 
      });
    }
  }, [isConnected]);

  // ====================================
  // 🔧 FONCTION DE FUSION INTELLIGENTE DES MESSAGES
  // ====================================

  const mergeMessagesIntelligently = useCallback((currentMessages, newMessages, source = 'api') => {
    console.log(`🔄 [mergeMessages] Source: ${source}, Current: ${currentMessages.length}, New: ${newMessages.length}`);
    
    if (newMessages.length === 0) {
      console.log('📝 [mergeMessages] Aucun nouveau message, conservation actuelle');
      return currentMessages;
    }
    
    // 🚨 DEBUG : Afficher les derniers messages pour comprendre
    console.log('🔍 [DEBUG] Derniers messages actuels:', 
      currentMessages.slice(-2).map(m => ({
        id: m.id || m.tempId,
        content: m.content?.substring(0, 20),
        isOptimistic: m.isOptimistic,
        created_at: m.created_at
      }))
    );
    
    console.log('🔍 [DEBUG] Derniers messages API:', 
      newMessages.slice(-2).map(m => ({
        id: m.id,
        content: m.content?.substring(0, 20),
        created_at: m.created_at
      }))
    );
    
    // ⚡ STRATÉGIE AGRESSIVE : Prioriser les messages optimistic récents
    const now = new Date();
    const recentThreshold = 30000; // 30 secondes
    
    // Créer un Map des messages API par ID
    const apiMessagesMap = new Map();
    newMessages.forEach(msg => {
      if (msg.id) {
        apiMessagesMap.set(msg.id, msg);
      }
    });
    
    // Créer un Map des messages API par contenu (pour matching optimistic)
    const apiMessagesByContent = new Map();
    newMessages.forEach(msg => {
      if (msg.content) {
        const key = `${msg.content.trim()}_${msg.sender_id}`;
        if (!apiMessagesByContent.has(key)) {
          apiMessagesByContent.set(key, []);
        }
        apiMessagesByContent.get(key).push(msg);
      }
    });
    
    const mergedMessages = [];
    const processedApiIds = new Set();
    const processedOptimisticIds = new Set();
    
    // 📊 ÉTAPE 1 : Traiter tous les messages de l'API d'abord
    newMessages.forEach(apiMessage => {
      if (!processedApiIds.has(apiMessage.id)) {
        mergedMessages.push(apiMessage);
        processedApiIds.add(apiMessage.id);
      }
    });
    
    // 📊 ÉTAPE 2 : Ajouter les messages optimistic RÉCENTS qui n'ont pas de correspondance
    currentMessages.forEach(currentMessage => {
      if (!currentMessage.isOptimistic) {
        return; // Ignorer les messages non-optimistic déjà traités
      }
      
      const messageAge = now - new Date(currentMessage.created_at);
      const isRecent = messageAge < recentThreshold;
      
      if (!isRecent) {
        console.log(`⏰ [mergeMessages] Message optimistic trop ancien ignoré (${Math.round(messageAge/1000)}s):`, 
          currentMessage.content?.substring(0, 20));
        return;
      }
      
      // Chercher correspondance par contenu ET timing
      const contentKey = `${currentMessage.content?.trim()}_${currentMessage.sender_id}`;
      const apiMatches = apiMessagesByContent.get(contentKey) || [];
      
      const hasApiMatch = apiMatches.some(apiMsg => {
        const timeDiff = Math.abs(new Date(apiMsg.created_at) - new Date(currentMessage.created_at));
        return timeDiff < 10000; // 10 secondes de tolérance
      });
      
      if (!hasApiMatch && !processedOptimisticIds.has(currentMessage.tempId)) {
        console.log(`✅ [mergeMessages] Conservation message optimistic récent:`, {
          tempId: currentMessage.tempId,
          content: currentMessage.content?.substring(0, 20),
          age: Math.round(messageAge/1000) + 's'
        });
        
        mergedMessages.push(currentMessage);
        processedOptimisticIds.add(currentMessage.tempId);
      } else if (hasApiMatch) {
        console.log(`🔄 [mergeMessages] Message optimistic trouvé dans API:`, 
          currentMessage.content?.substring(0, 20));
      }
    });
    
    // 📊 ÉTAPE 3 : Trier par date et nettoyer
    const sortedMessages = mergedMessages
      .filter(msg => msg && (msg.id || msg.tempId)) // Supprimer les messages invalides
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
    
    const optimisticCount = sortedMessages.filter(m => m.isOptimistic).length;
    
    console.log(`✅ [mergeMessages] Résultat final: ${sortedMessages.length} messages (${optimisticCount} optimistic)`);
    
    // 🔍 DEBUG : Afficher le résultat final
    console.log('🎯 [DEBUG] Messages finaux:', 
      sortedMessages.slice(-3).map(m => ({
        id: m.id || m.tempId,
        content: m.content?.substring(0, 20),
        isOptimistic: m.isOptimistic,
        created_at: m.created_at
      }))
    );
    
    return sortedMessages;
  }, []);

  // ====================================
  // HANDLERS SOCKET (DÉCLARER APRÈS LES UTILITAIRES)
  // ====================================

  const handleNewMessageCorrected = useCallback((data) => {
    const { message, sender, tempId } = data;
    console.log('📩 [Socket] Nouveau message reçu:', message.id, 'tempId:', tempId);
    
    // Marquer qu'une update Socket est en cours
    isSocketUpdateRef.current = true;
    
    const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
    const isOwnMessage = message.sender_id === currentUser.id;

    setMessages(prev => {
      let updatedMessages;
      
      if (isOwnMessage && tempId) {
        console.log('🔄 [Socket] Remplacement optimistic → réel:', tempId, '→', message.id);
        
        updatedMessages = prev.map(msg => {
          if (msg.isOptimistic && (msg.tempId === tempId || msg.id === tempId)) {
            return {
              ...message,
              senderName: `${sender.prenom} ${sender.nom}`,
              isOwn: true,
              isOptimistic: false
            };
          }
          return msg;
        });
      } else if (!isOwnMessage) {
        const exists = prev.some(msg => msg.id === message.id);
        if (exists) {
          console.log('🚫 [Socket] Message déjà présent, ignoré:', message.id);
          updatedMessages = prev;
        } else {
          console.log('✅ [Socket] Ajout nouveau message externe:', message.id);
          const newMessage = {
            ...message,
            senderName: `${sender.prenom} ${sender.nom}`,
            isOwn: false
          };
          
          updatedMessages = [...prev, newMessage];
        }
      } else {
        console.log('🚫 [Socket] Message propre sans tempId ignoré:', message.id);
        updatedMessages = prev;
      }
      
      // Mettre à jour la référence
      lastKnownMessagesRef.current = updatedMessages;
      
      // Reset flag après délai
      setTimeout(() => {
        isSocketUpdateRef.current = false;
      }, 3000);
      
      return updatedMessages;
    });

    // Auto-marquage si conversation active et message externe
    if (!isOwnMessage && selectedConversation?.id === message.conversation_id) {
      console.log('⚡ [Socket] Auto-marquage message externe:', message.id);
      setTimeout(() => {
        markMessageAsReadRealtime(message.id, message.conversation_id);
      }, 500);
    }

    scrollToBottom();
  }, [selectedConversation?.id, markMessageAsReadRealtime, scrollToBottom]);

  const handleMessageReadConfirmed = useCallback((data) => {
    const { messageId, conversationId, readBy, readAt, success } = data;
    
    if (!success) {
      console.warn(`⚠️ [Socket] Échec marquage lecture message ${messageId}`);
      return;
    }

    console.log(`✅ [Socket] Confirmation lecture message ${messageId} par ${readBy.prenom}`);
    
    setMessageReadStatus(prev => new Map(prev.set(messageId, {
      isRead: true,
      readAt: readAt,
      readBy: readBy
    })));

    setMessages(prev => prev.map(msg => 
      msg.id === messageId 
        ? { ...msg, read_count: (msg.read_count || 0) + 1 }
        : msg
    ));
  }, []);

  const handleConversationMessagesMarked = useCallback((data) => {
    const { conversationId, readBy, readAt, messagesMarked, success } = data;
    
    if (!success) {
      console.warn(`⚠️ [Socket] Échec marquage conversation ${conversationId}`);
      return;
    }

    console.log(`✅ [Socket] Conversation ${conversationId} marquée: ${messagesMarked} messages par ${readBy.prenom}`);
    
    setMessages(prev => prev.map(msg => {
      if (msg.conversation_id === conversationId && msg.sender_id !== readBy.id) {
        setMessageReadStatus(prevStatus => new Map(prevStatus.set(msg.id, {
          isRead: true,
          readAt: readAt,
          readBy: readBy
        })));
        
        return { ...msg, read_count: (msg.read_count || 0) + 1 };
      }
      return msg;
    }));
  }, []);

  const handleUserTyping = (data) => {
    const { conversationId, employe, isTyping } = data;
    
    if (selectedConversation?.id === conversationId) {
      if (isTyping) {
        setTypingUsers(prev => {
          const exists = prev.some(user => user.id === employe.id);
          return exists ? prev : [...prev, employe];
        });
      } else {
        setTypingUsers(prev => prev.filter(user => user.id !== employe.id));
      }
    }
  };

  const handleUserOnline = (data) => {
    const { employeId } = data;
    setOnlineUsers(prev => {
      const newSet = new Set(prev);
      newSet.add(employeId);
      return newSet;
    });
  };

  const handleUserOffline = (data) => {
    const { employeId } = data;
    setOnlineUsers(prev => {
      const newSet = new Set(prev);
      newSet.delete(employeId);
      return newSet;
    });
  };

  const handleJoinedConversation = useCallback((data) => {
    const { conversationId, success } = data;
    console.log(`✅ [Socket] Conversation rejointe: ${conversationId}`);
    
    if (success && selectedConversation?.id === conversationId) {
      markConversationAsReadRealtime(conversationId);
    }
  }, [selectedConversation?.id, markConversationAsReadRealtime]);

  const handleJoinConversationError = useCallback((data) => {
    const { conversationId, error } = data;
    console.error(`❌ [Socket] Erreur jointure conversation ${conversationId}:`, error);
    setError(`Impossible d'accéder à la conversation: ${error}`);
  }, []);

  const handleMessageSendError = useCallback((data) => {
    const { tempId, error } = data;
    console.error(`❌ [Socket] Erreur envoi message ${tempId}:`, error);
    
    setMessages(prev => prev.filter(msg => 
      !(msg.isOptimistic && (msg.tempId === tempId || msg.id === tempId))
    ));
    
    setError(`Erreur envoi message: ${error}`);
  }, []);

  // ====================================
  // 🆕 SYSTÈME DE POLLING DE SÉCURITÉ
  // ====================================

  const fetchMessagesSafely = useCallback(async (conversationId, showLoading = false) => {
    if (!conversationId || syncInProgressRef.current) return;
    
    syncInProgressRef.current = true;
    
    try {
      console.log(`🛡️ [HybridMessaging] Polling sécurité messages conversation ${conversationId}`);
      
      if (showLoading) setLoading(true);
      
      if (typeof conversationId === 'string' && conversationId.startsWith('group_')) {
        console.log('📋 [HybridMessaging] Groupe sans conversation, skip polling');
        return;
      }
      
      // 🚨 NOUVEAU : Vérifier si on a des messages optimistic récents
      const hasRecentOptimistic = messages.some(msg => {
        if (!msg.isOptimistic) return false;
        const age = new Date() - new Date(msg.created_at);
        return age < 30000; // 30 secondes
      });
      
      if (hasRecentOptimistic && isSocketUpdateRef.current) {
        console.log('⏸️ [HybridMessaging] Messages optimistic récents + Socket actif → Skip polling pour éviter conflit');
        return;
      }
      
      const result = await api.messagerie.getMessages(conversationId);
      
      if (result.success && isComponentMountedRef.current) {
        const newMessages = result.data || [];
        
        console.log(`📊 [fetchMessagesSafely] Messages reçus de l'API: ${newMessages.length}`);
        
        // Fusion intelligente avec protection optimistic
        setMessages(currentMessages => {
          const mergedMessages = mergeMessagesIntelligently(currentMessages, newMessages, 'polling');
          lastKnownMessagesRef.current = mergedMessages;
          return mergedMessages;
        });
        
        lastSyncTimestampRef.current = new Date().toISOString();
        console.log(`✅ [HybridMessaging] Messages synchronisés via polling`);
      }
    } catch (error) {
      console.warn('⚠️ [HybridMessaging] Erreur polling messages:', error);
    } finally {
      syncInProgressRef.current = false;
      if (showLoading) setLoading(false);
    }
  }, [mergeMessagesIntelligently]);

  const fetchConversationsSafely = useCallback(async () => {
    if (syncInProgressRef.current) return;
    
    syncInProgressRef.current = true;
    
    try {
      console.log(`🛡️ [HybridMessaging] Polling sécurité conversations`);
      
      const dashboardResult = await api.messagerie.getDashboard();
      
      if (dashboardResult.success && isComponentMountedRef.current) {
        const data = dashboardResult.data;
        
        const individualConversationsOnly = (data.conversations || []).filter(conv => {
          if (conv.type === 'group') return false;
          if ((conv.name || conv.display_name || '').includes('💬')) return false;
          return true;
        });
        
        setConversations(prev => {
          if (JSON.stringify(prev.map(c => c.id)) === JSON.stringify(individualConversationsOnly.map(c => c.id))) {
            return prev;
          }
          
          console.log(`✅ [HybridMessaging] ${individualConversationsOnly.length} conversations synchronisées (polling)`);
          return individualConversationsOnly;
        });
        
        const totalUnread = individualConversationsOnly.reduce((total, conv) => 
          total + (conv.unread_count || 0), 0
        );
        onUnreadCountChange?.(totalUnread);
      }
    } catch (error) {
      console.warn('⚠️ [HybridMessaging] Erreur polling conversations:', error);
    } finally {
      syncInProgressRef.current = false;
    }
  }, [onUnreadCountChange]);

  // ✅ CORRECTION : startSecurityPolling avec dépendances vides
  const startSecurityPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
    }

    pollingIntervalRef.current = setInterval(() => {
      if (isComponentMountedRef.current && !syncInProgressRef.current) {
        console.log('🛡️ [HybridMessaging] Polling de sécurité déclenché...');
        
        // Utiliser la ref pour selectedConversation
        const currentConversation = selectedConversationRef.current;
        if (currentConversation?.id) {
          handlersRef.current.fetchMessagesSafely?.(currentConversation.id, false);
        }
        
        handlersRef.current.fetchConversationsSafely?.();
      }
    }, POLLING_INTERVAL);

    console.log('🛡️ [HybridMessaging] Polling de sécurité démarré (30s)');
  }, []); // ✅ DÉPENDANCES VIDES !

  const stopSecurityPolling = useCallback(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
      console.log('⏹️ [HybridMessaging] Polling de sécurité arrêté');
    }
  }, []);

  // ====================================
  // GESTION SOCKET.IO
  // ====================================

  const cleanupSocket = useCallback(() => {
    console.log('🧹 [HybridMessaging] Nettoyage Socket...');
    
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    if (socketRef.current) {
      // Supprimer TOUS les listeners trackés
      socketEventListenersRef.current.forEach(eventName => {
        socketRef.current.off(eventName);
        console.log(`🗑️ [Socket] Removed listener: ${eventName}`);
      });
      socketEventListenersRef.current.clear();
      
      // Déconnexion propre
      if (socketRef.current.connected) {
        console.log('🔌 [Socket] Déconnexion propre...');
        socketRef.current.disconnect();
      }
      
      socketRef.current = null;
    }
    
    isConnectingRef.current = false;
    setIsConnected(false);
  }, []);

  // ================================
  // HELPER POUR AJOUTER DES LISTENERS TRACKÉS
  // ================================
  const addSocketListener = useCallback((eventName, handler) => {
    if (socketRef.current) {
      socketRef.current.on(eventName, handler);
      socketEventListenersRef.current.add(eventName);
      console.log(`✅ [Socket] Added listener: ${eventName}`);
    }
  }, []);

  // ================================
  // LOGIQUE DE RECONNEXION INTELLIGENTE
  // ================================
  const shouldReconnect = useCallback((reason) => {
    const noReconnectReasons = [
      'io server disconnect', // Serveur fermé volontairement
      'transport close', // Transport fermé proprement
      'client namespace disconnect' // Client déconnecté volontairement
    ];
    
    return !noReconnectReasons.includes(reason);
  }, []);

  // ✅ CORRECTION : connectSocket avec dépendances vides
  const connectSocket = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('❌ [HybridMessaging] Pas de token, connexion Socket ignorée');
      return;
    }

    if (isConnectingRef.current || socketRef.current?.connected) {
      console.log('⏸️ [Socket] Déjà en cours de connexion ou connecté');
      return;
    }

    isConnectingRef.current = true;
    console.log('🔌 [HybridMessaging] Connexion Socket.io...');
    setIsReconnecting(true);

    // Nettoyage préventif
    handlersRef.current.cleanupSocket?.();

  // ✅ Détection automatique du domaine en production aussi
    const socketUrl = window.location.origin;

    socketRef.current = io(socketUrl, {
      auth: { token },
      path: '/socket.io',  // ✅ IMPORTANT pour le proxy

      
      transports: ['websocket', 'polling'],
      upgrade: true,
      rememberUpgrade: true,
      timeout: 10000,
      reconnection: false,
      forceNew: false,
      autoConnect: true
    });

    // Fonction locale scheduleReconnection
    const scheduleReconnection = (reason) => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      
      const getReconnectDelay = (reason) => {
        switch (reason) {
          case 'ping timeout': return 1000;
          case 'transport error': return 3000;
          case 'connect_error': return 5000;
          default: return 2000;
        }
      };
      
      const delay = getReconnectDelay(reason);
      
      console.log(`⏰ [Socket.io] Reconnexion programmée dans ${delay}ms (raison: ${reason})`);
      
      reconnectTimeoutRef.current = setTimeout(() => {
        if (isComponentMountedRef.current && !socketRef.current?.connected) {
          console.log('🔄 [Socket.io] Tentative de reconnexion...');
          connectSocket();
        }
      }, delay);
    };

    // Helper pour ajouter listeners
    const addListener = (eventName, handler) => {
      if (socketRef.current) {
        socketRef.current.on(eventName, handler);
        socketEventListenersRef.current.add(eventName);
        console.log(`✅ [Socket] Added listener: ${eventName}`);
      }
    };

    // CONNEXION RÉUSSIE
    addListener('connect', () => {
      console.log('🟢 [Socket.io] Connecté avec succès');
      isConnectingRef.current = false;
      setIsConnected(true);
      setIsReconnecting(false);
      setError(null);
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
        reconnectTimeoutRef.current = null;
      }

      // Utiliser ref pour selectedConversation
      const currentConversation = selectedConversationRef.current;
      if (currentConversation?.id) {
        console.log('🔄 [HybridMessaging] Vérification post-connexion...');
        setTimeout(() => {
          if (!isSocketUpdateRef.current) {
            handlersRef.current.fetchMessagesSafely?.(currentConversation.id, false);
          }
        }, 2000);
      }
    });

    // DÉCONNEXION
    addListener('disconnect', (reason) => {
      console.log(`🔴 [Socket.io] Déconnecté - Raison: ${reason}`);
      lastDisconnectReasonRef.current = reason;
      setIsConnected(false);
      isConnectingRef.current = false;
      
      if (isComponentMountedRef.current && handlersRef.current.shouldReconnect?.(reason)) {
        scheduleReconnection(reason);
      }
    });

    // ERREURS DE CONNEXION
    addListener('connect_error', (error) => {
      console.error('❌ [HybridMessaging] Erreur connexion Socket.io:', error);
      setError('Erreur de connexion temps réel');
      setIsConnected(false);
      isConnectingRef.current = false;
      
      if (isComponentMountedRef.current) {
        scheduleReconnection('connect_error');
      }
    });

    // RECONNEXION RÉUSSIE
    addListener('reconnect', (attemptNumber) => {
      console.log(`🔄 [HybridMessaging] Reconnecté après ${attemptNumber} tentative(s)`);
      setIsReconnecting(false);
      
      const currentConversation = selectedConversationRef.current;
      if (currentConversation?.id) {
        console.log('🔄 [HybridMessaging] Récupération messages manqués...');
        setTimeout(() => {
          handlersRef.current.fetchMessagesSafely?.(currentConversation.id, false);
        }, 1000);
      }
    });

    // ÉCHEC RECONNEXION
    addListener('reconnect_error', (error) => {
      console.error('❌ [HybridMessaging] Erreur reconnexion:', error);
      setIsReconnecting(true);
    });

    addListener('reconnect_failed', () => {
      console.error('❌ [HybridMessaging] Reconnexion échouée après max tentatives');
      setIsReconnecting(false);
      setError('Connexion temps réel impossible, basculement en mode polling');
      handlersRef.current.startSecurityPolling?.();
    });

    // ÉVÉNEMENTS MESSAGERIE - utiliser les refs
    addListener('new_message', handlersRef.current.handleNewMessageCorrected);
    addListener('message_read_status', handlersRef.current.handleMessageReadConfirmed);
    addListener('conversation_messages_marked', handlersRef.current.handleConversationMessagesMarked);
    addListener('user_typing', handleUserTyping);
    addListener('user_online', handleUserOnline);
    addListener('user_offline', handleUserOffline);
    addListener('joined_conversation', handlersRef.current.handleJoinedConversation);
    addListener('join_conversation_error', handlersRef.current.handleJoinConversationError);
    addListener('message_send_error', handlersRef.current.handleMessageSendError);
    // ✅ NOUVEAU : Écouter les mises à jour de conversations

addListener('conversation_updated', (data) => {
  console.log('📋 [Socket] conversation_updated reçu:', data);
  
  const { conversation, lastMessage } = data;
  
  // 📎 Déterminer le contenu à afficher selon le type de message
  let displayContent = lastMessage.content || '';
  
  if (lastMessage.message_type === 'file' || (lastMessage.attachments && lastMessage.attachments.length > 0)) {
    // Message avec fichier(s)
    const fileCount = lastMessage.attachments?.length || 1;
    displayContent = fileCount === 1 
      ? '📎 Pièce jointe' 
      : `📎 ${fileCount} pièces jointes`;
  } else if (!displayContent || displayContent.trim() === '') {
    // Message vide
    displayContent = 'Message envoyé';
  }
  
  // Mettre à jour la liste des conversations
  setConversations(prev => {
    const updated = prev.map(conv => 
      conv.id === conversation.id 
        ? {
            ...conv,
            ...conversation,
            last_message_content: displayContent, // ✅ MAINTENANT AVEC LOGIQUE FICHIERS
            last_message_at: lastMessage.created_at,
            unread_count: conversation.unread_count
          }
        : conv
    );
    
    // Trier par dernier message (plus récent en premier)
    return updated.sort((a, b) => 
      new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0)
    );
  });
  
  // Mettre à jour le compteur total non-lus
  setTimeout(() => {
    setConversations(currentConvs => {
      const totalUnread = currentConvs.reduce((total, conv) => 
        total + (conv.unread_count || 0), 0
      );
      onUnreadCountChange?.(totalUnread);
      return currentConvs;
    });
  }, 100);
});

  }, []); // ✅ DÉPENDANCES VIDES !

  const disconnectSocket = useCallback(() => {
    if (socketRef.current) {
      console.log('🔌 [HybridMessaging] Déconnexion Socket...');
      cleanupSocket();
    }
    setIsConnected(false);
  }, [cleanupSocket]);

  // ====================================
  // FONCTIONS MÉTIER PRINCIPALES
  // ====================================

  const fetchMessages = useCallback(async (conversationId) => {
    if (loading) {
      console.log('⚠️ [fetchMessages] Chargement déjà en cours - ignoré');
      return;
    }

    if (!conversationId || conversationId === 'undefined' || conversationId === 'null') {
      console.error('❌ [fetchMessages] ID conversation invalide:', conversationId);
      setMessages([]);
      lastKnownMessagesRef.current = [];
      return;
    }

    try {
      setLoading(true);
      setError(null);
      
      console.log(`🔍 [fetchMessages] START - conversationId: ${conversationId}`);
      
      if (typeof conversationId === 'string' && conversationId.startsWith('group_')) {
        console.log('📋 [fetchMessages] Groupe sans conversation, aucun message à charger');
        setMessages([]);
        lastKnownMessagesRef.current = [];
        return;
      }
      
      if (selectedConversation?.type === 'group' && !selectedConversation?.isGroupWithoutConversation) {
        console.error(`🚫 [PROTECTION fetchMessages] Tentative de charger messages d'un groupe: ${selectedConversation.name}`);
        setError('Impossible de charger les messages de ce groupe');
        setMessages([]);
        lastKnownMessagesRef.current = [];
        return;
      }
      
      const result = await api.messagerie.getMessages(conversationId);
      
      if (!result.success) {
        throw new Error(result.error || 'Erreur chargement messages');
      }
      
      const messages = result.data || [];
      console.log(`✅ [fetchMessages] ${messages.length} messages chargés pour conversation: ${conversationId}`);
      
      setMessages(messages);
      lastKnownMessagesRef.current = messages;
      
    } catch (error) {
      console.error('❌ [fetchMessages] ERROR:', error);
      
      if (error.response?.status === 404) {
        setError('Conversation non trouvée');
      } else if (error.response?.status === 403) {
        setError('Accès refusé à cette conversation');
      } else if (error.response?.status === 401) {
        setError('Session expirée, veuillez vous reconnecter');
      } else {
        setError('Impossible de charger les messages');
      }
      
      setMessages([]);
      lastKnownMessagesRef.current = [];
      
    } finally {
      setLoading(false);
      console.log(`🔚 [fetchMessages] END - conversationId: ${conversationId}`);
    }
  }, [loading, selectedConversation]);

  const sendMessage = useCallback(async (content, attachments = []) => {
    if (!content.trim() && attachments.length === 0) return;
    if (!selectedConversation) return;

    let tempId;

    try {
      const messageContent = content.trim();
      const messageAttachments = [...attachments];
      
      tempId = `temp_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
      
      const optimisticMessage = {
        id: tempId,
        tempId: tempId,
        content: messageContent,
        sender_id: currentUser.id,
        conversation_id: selectedConversation.id,
        created_at: new Date().toISOString(),
        attachments: messageAttachments,
        senderName: `${currentUser.prenom} ${currentUser.nom}`,
        isOwn: true,
        isOptimistic: true,
        read_count: 0
      };

      // Marquer update Socket pour éviter conflit polling
      isSocketUpdateRef.current = true;
      
      setMessages(prev => {
        const updated = [...prev, optimisticMessage];
        lastKnownMessagesRef.current = updated;
        return updated;
      });

      if (messageAttachments.length > 0) {
        console.log(`📎 [sendMessage] Envoi avec ${messageAttachments.length} fichier(s) via API`);
        
        const result = await api.messagerie.sendMessageWithFiles(selectedConversation.id, {
          content: messageContent,
          messageType: 'file',
          attachments: messageAttachments.map(att => att.file),
          tempId: tempId
        });

        if (!result.success) {
          throw new Error(result.error || 'Erreur envoi message avec fichiers');
        }

        setMessages(prev => {
          const updated = prev.map(msg => 
            msg.tempId === tempId ? {
              ...result.data,
              isOwn: true,
              senderName: `${currentUser.prenom} ${currentUser.nom}`,
              isOptimistic: false
            } : msg
          );
          lastKnownMessagesRef.current = updated;
          return updated;
        });

      } else {
        if (socketRef.current?.connected) {
          console.log(`📤 [Socket] Émission new_message texte avec tempId: ${tempId}`);
          
          socketRef.current.emit('new_message', {
            conversationId: selectedConversation.id,
            content: messageContent,
            messageType: 'text',
            tempId: tempId
          });
        } else {
          throw new Error('Pas de connexion Socket.io');
        }
      }
      
      scrollToBottom();
      
      // Reset flag après délai
      setTimeout(() => {
        isSocketUpdateRef.current = false;
      }, 3000);
      
    } catch (error) {
      console.error('❌ [sendMessage] Erreur:', error);
      
      if (tempId) {
        setMessages(prev => {
          const updated = prev.filter(msg => !msg.isOptimistic || msg.tempId !== tempId);
          lastKnownMessagesRef.current = updated;
          return updated;
        });
      }
      setError('Impossible d\'envoyer le message');
      
      // Reset flag en cas d'erreur
      isSocketUpdateRef.current = false;
    }
  }, [selectedConversation, scrollToBottom]);

  const openConversation = useCallback(async (conversation) => {
    if (conversation.type === 'group') {
      console.error(`🚫 [openConversation] Tentative d'ouvrir un groupe: ${conversation.name}`);
      return;
    }

    console.log(`🚀 [openConversation] Ouverture conversation: ${conversation.id}`);

    try {
      setSelectedConversation(conversation);

      if (socketRef.current?.connected) {
        socketRef.current.emit('join_conversation', { 
          conversationId: conversation.id 
        });
      }

      await fetchMessages(conversation.id);

      if (socketRef.current?.connected) {
        socketRef.current.emit('mark_conversation_messages_read_realtime', {
          conversationId: conversation.id,
          readAt: new Date().toISOString()
        });
      }

      console.log(`✅ [openConversation] Conversation ${conversation.id} ouverte et marquée comme lue`);

    } catch (error) {
      console.error('❌ [openConversation] Erreur:', error);
      setError('Impossible d\'ouvrir la conversation');
    }
  }, [fetchMessages]);

  // ✅ CORRECTION : forceSync avec dépendances vides
  const forceSync = useCallback(() => {
    console.log('🔄 [HybridMessaging] Synchronisation forcée...');
    
    // Autoriser sync forcée
    isSocketUpdateRef.current = false;
    
    // Utiliser les refs pour éviter les dépendances
    const currentConversation = selectedConversationRef.current;
    if (currentConversation?.id) {
      handlersRef.current.fetchMessagesSafely?.(currentConversation.id, true);
    }
    handlersRef.current.fetchConversationsSafely?.();
  }, []); // ✅ DÉPENDANCES VIDES !

  // ====================================
  // ✅ MISE À JOUR DES RÉFÉRENCES
  // ====================================

  // Mettre à jour selectedConversation ref
  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  // Mettre à jour handlers ref
  useEffect(() => {
    handlersRef.current = {
      handleNewMessageCorrected,
      handleMessageReadConfirmed,
      handleConversationMessagesMarked,
      handleJoinedConversation,
      handleJoinConversationError,
      handleMessageSendError,
      startSecurityPolling,
      fetchMessagesSafely,
      fetchConversationsSafely, // ✅ AJOUTÉ !
      cleanupSocket,
      addSocketListener,
      shouldReconnect
    };
  }); // ✅ Pas de dépendances pour éviter les re-renders

  // ====================================
  // GESTION FOCUS/VISIBILITÉ
  // ====================================

  // ✅ CORRECTION : useEffect focus/visibilité avec dépendances vides
  useEffect(() => {
    if (!SYNC_ON_FOCUS) return;

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && isComponentMountedRef.current) {
        console.log('🔄 [HybridMessaging] Page visible, synchronisation...');
        
        // Reset flag pour autoriser sync
        isSocketUpdateRef.current = false;
        
        const currentConversation = selectedConversationRef.current;
        if (currentConversation?.id) {
          handlersRef.current.fetchMessagesSafely?.(currentConversation.id, false);
        }
        handlersRef.current.fetchConversationsSafely?.();
      }
    };

    const handleFocus = () => {
      if (isComponentMountedRef.current) {
        console.log('🔄 [HybridMessaging] Focus reçu, synchronisation...');
        
        // Reset flag pour autoriser sync
        isSocketUpdateRef.current = false;
        
        const currentConversation = selectedConversationRef.current;
        if (currentConversation?.id) {
          handlersRef.current.fetchMessagesSafely?.(currentConversation.id, false);
        }
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleFocus);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleFocus);
    };
  }, []); // ✅ DÉPENDANCES VIDES !

  // ====================================
  // CYCLE DE VIE DU COMPOSANT
  // ====================================

  useEffect(() => {
    isComponentMountedRef.current = true;

    const token = localStorage.getItem('token');
    if (token) {
      connectSocket();
      startSecurityPolling();
    }

    return () => {
      isComponentMountedRef.current = false;
      disconnectSocket();
      stopSecurityPolling();
      
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, [connectSocket, disconnectSocket, startSecurityPolling, stopSecurityPolling]);

  // ====================================
  // RETOUR DU HOOK
  // ====================================

  return {
    // États principaux
    conversations,
    setConversations,
    groups,
    setGroups,
    employees,
    setEmployees,
    messages,
    setMessages,
    selectedConversation,
    setSelectedConversation,
    loading,
    setLoading,
    error,
    setError,

    // États temps réel
    isConnected,
    isReconnecting,
    typingUsers,
    setTypingUsers,
    onlineUsers,
    messageReadStatus,
    setMessageReadStatus,

    // États groupes et archivage
    groupConversations,
    setGroupConversations,
    groupMessages,
    setGroupMessages,
    archivedConversations,
    setArchivedConversations,

    // Références
    socketRef,
    messagesEndRef,
    typingTimeoutRef,

    // Actions principales
    fetchMessages,
    sendMessage,
    openConversation,
    joinConversation,
    leaveConversation,
    
    // Actions Socket
    markMessageAsReadRealtime,
    markConversationAsReadRealtime,
    
    // Utilitaires
    scrollToBottom,
    isUserOnline,
    forceSync,
    connectSocket,
    disconnectSocket,

    // Nouvelles fonctions hybrides
    fetchMessagesSafely,
    fetchConversationsSafely,
    startSecurityPolling,
    stopSecurityPolling,

    // Statut hybride
    lastSyncTimestamp: lastSyncTimestampRef.current,
    syncInProgress: syncInProgressRef.current
  };
};

export default useHybridMessaging;